import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  Input,
  OnDestroy,
  EventEmitter,
  Output,
} from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { Subject } from "rxjs";
import { RenewQuoteService } from "src/app/main/quoting/renew-quote/renewquote.service";
import { EpAppStore } from "src/app/shared/services/epAppStoreService/epAppStore.service";
import { takeUntil, tap } from "rxjs/operators";
import { EpQuotesService } from "src/app/shared/services/epQuotesService/ep-quotes.service";
import { EpErrorHandlerService } from "src/app/shared/services/epErrorHandlerService/epErrorHandler.service";

@Component({
  selector: "app-plan-admin-product-panel",
  templateUrl: "./plan-admin-product-panel.component.html",
  styleUrls: ["./plan-admin-product-panel.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanAdminProductPanelComponent implements OnInit, OnDestroy {
  @Input() public group: any;
  @Input() public canEdit: boolean;
  @Input() public viewPage: string;
  @Output() hideSumbitRenewals = new EventEmitter<boolean>();
  isEditPA = false;
  isEditAssociation = false;
  contributions = ["Unknown", "Non-Voluntary (Employer Paid)", "Voluntary"];
  planForm: FormGroup;
  editMode = false;
  isLoadingScreen = false;
  isError = false;
  associationCounties = ["cambria", "blair", "bedford", "indiana", "somerset"];
  private unsubscribeAll = new Subject();
  dentalContribution = "";
  visionContribution = "";
  dentalDisplay = "";
  showCobraPopup = false;
  showCobraAdmin = false;

  constructor(
    private renewQuoteService: RenewQuoteService,
    private storeService: EpAppStore,
    private quoteService: EpQuotesService,
    private errorHandler: EpErrorHandlerService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.isLoadingScreen = true;
    const { countyName, subStatus } =
      this.storeService.select("currentGroup").value;
    this.canEdit = subStatus === "Renewed" ? false : true;
    this.isEditAssociation =
      countyName &&
      this.associationCounties.indexOf(countyName.toLowerCase()) > -1
        ? true
        : false;
    this.planForm = new FormGroup({
      visionContribution: new FormControl(null),
      dentalContribution: new FormControl(null),
      currentDentalCoverage: new FormControl(),
      paAct4: new FormControl(),
      dental: new FormControl(),
      domesticPartnership: new FormControl(),
      association: new FormControl(),
      fsaQuoted: new FormControl(),
      cobraType: new FormControl(),
      cobraAdmin: new FormControl(),
      wellness: new FormControl(),
      workersComp: new FormControl(),
      hippaStatus: new FormControl(),
      hsa: new FormControl(),
    });
    // Convert boolean/string nonUPMCDental to proper Yes/No string
    const dentalValue =
      this.group.currentDentalCoverage === "Yes"
        ? "No"
        : this.group.nonUPMCDental === true ||
            this.group.nonUPMCDental === "Yes"
          ? "Yes"
          : "No";

    this.dentalDisplay = dentalValue;
    this.group.nonUPMCDental = dentalValue;

    // Patch initial form values for radio groups
    this.planForm.patchValue({
      paAct4: this.group.paAct4 ? "Yes" : "No",
      domesticPartnership: this.group.domesticPartner ? "Yes" : "No",
      association: this.group.association ? "Yes" : "No",
      dental: dentalValue,
      fsaQuoted: this.group.fsa ? "Yes" : "No",
      cobraType: this.group.cobra || "None",
      cobraAdmin: this.group.isCobraAdmin ? "Yes" : "No",
    });

    // Set initial showCobraAdmin state
    this.showCobraAdmin = this.group.cobra === "Cobra";

    const quoteID = this.storeService.select("currentQuote").value.quoteId;
    if (quoteID) {
      this.quoteService
        .getQuote(quoteID)
        .pipe(takeUntil(this.unsubscribeAll))
        .subscribe(
          (response) => {
            if (response) {
              this.dentalContribution = response.dentalContributionStatus;
              this.visionContribution = response.visionContributionStatus;
              this.isLoadingScreen = false;
            }
            this.cdr.markForCheck();
          },
          () => {
            this.isLoadingScreen = false;
            this.errorHandler.push(
              "There was a problem dental and vision contribution values.",
            );
            this.cdr.markForCheck();
          },
        );
    } else {
      this.dentalContribution = this.group.dentalContribution;
      this.visionContribution = this.group.visionContribution;
      this.isLoadingScreen = false;
    }
  }

  onSubmitGroupInfo() {
    this.isLoadingScreen = true;
    const quoteID =
      this.group?.quoteId ||
      this.storeService.select("currentQuote").value.quoteId;
    const paAct4 = this.planForm.value.paAct4 === "Yes";
    const dental = this.planForm.value.dental;
    const association = this.planForm.value.association === "Yes";
    const domesticPartnership =
      this.planForm.value.domesticPartnership === "Yes";
    const body = {
      quoteId: quoteID,
      AssociationCode: association,
      GroupAgePolicy: paAct4,
      IsNonUPMCDental: dental === "Yes" ? true : false,
      IsDomesticPartnership: domesticPartnership,
    };
    this.isError = false;
    this.renewQuoteService
      .setAlternateQuote(body, quoteID)
      .pipe(
        tap(() => (this.isLoadingScreen = false)),
        takeUntil(this.unsubscribeAll),
      )
      .subscribe(
        (response) => {
          if (response) {
            this.group.association = association;
            this.group.paAct4 = paAct4;
            this.group.nonUPMCDental = dental;
            this.group.domesticPartner = domesticPartnership;
            const currentQuote = this.storeService.select("currentQuote").value;
            const isRenewalQuote = currentQuote.quoteType === "RENEWAL";
            currentQuote.isAlternateRenewal = true;
            currentQuote.isDomesticPartnership = domesticPartnership;
            currentQuote.nonUPMCDental = dental === "Yes" ? true : false;
            this.storeService.set("currentQuote", {
              ...currentQuote,
              isRenewalQuote,
            });
            if (this.hideSumbitRenewals) {
              this.hideSumbitRenewals.emit(true);
            }
            this.onCancel();
          }
          this.cdr.markForCheck();
        },
        () => {
          this.isError = true;
          this.isLoadingScreen = false;
          this.cdr.markForCheck();
        },
      );
  }

  onCancel() {
    this.editMode = false;
    this.isEditPA = false;
    this.isError = false;
    this.showCobraPopup = false;
    this.showCobraAdmin = false;
  }

  onCobraChange(event: any) {
    const selectedValue = event.target.value;

    if (selectedValue === "Cobra") {
      this.showCobraPopup = true;
      this.showCobraAdmin = true;
    } else {
      this.showCobraAdmin = false;
      this.planForm.patchValue({ cobraAdmin: "No" });
    }
  }

  closeCobraPopup() {
    this.showCobraPopup = false;
  }

  ngOnDestroy() {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  // onEdit() {
  //   this.editMode = true;
  // }

  onEditPA() {
    const paAct4 = this.group.paAct4 ? "Yes" : "No";
    const domesticPartner = this.group.domesticPartner ? "Yes" : "No";
    const association = this.group.association ? "Yes" : "No";
    const dental =
      this.group.nonUPMCDental === true || this.group.nonUPMCDental === "Yes"
        ? "Yes"
        : "No";
    this.planForm.get("paAct4").setValue(paAct4);
    this.planForm.get("domesticPartnership").setValue(domesticPartner);
    this.planForm.get("association").setValue(association);
    this.planForm.get("dental").setValue(dental);
    this.isEditPA = true;
  }
}
